import { createRoot } from 'react-dom/client';
import { App } from './App';
import { Crash } from './Crash';
/*
 * The typeface, self-hosted.
 *
 * --font once named a face without anything ever loading it, so every phone
 * quietly fell through to system-ui -- SF Pro on an iPhone, Roboto on a
 * Pixel. The app looked like whatever phone it was on.
 *
 * Inter, which replaced Plus Jakarta Sans for being easier to read. Messages
 * themselves are in the phone's own face (--font-read), which needs nothing
 * loaded. Upright only, and the variable cut, so every weight is one file and
 * font-weight: 650 means 650 rather than rounding to the nearest of two.
 * Subsets are split by unicode-range, so only Latin is fetched; Vite hashes
 * the woff2 into /assets/, which the service worker caches first.
 */
import '@fontsource-variable/inter/wght.css';
/*
 * The faces words on a snap can be set in (shared/captionStyle.ts), in the
 * weights the caption is drawn at. Only the @font-face rules load here; each
 * font file is fetched the first time a caption on screen asks for it.
 */
import '@fontsource/playfair-display/700.css';
import '@fontsource/permanent-marker/400.css';
import '@fontsource/caveat/700.css';
import '@fontsource/courier-prime/700.css';
import './theme.css';
import { registerServiceWorker } from './notifications';
import { initTheme } from './theme';
import { plantHome } from './router';
import { resyncOnResume } from './resync';
import { watchBrokenMedia } from './mediaRetry';
import { MEDIA_GATEWAY } from './firebase';
import { keepMediaSessionFresh, renewMediaSession } from './session';
import { resumeUploads, watchForResume } from './uploadQueue';

// Before render, so a light-mode user never sees a flash of the dark palette.
initTheme();
// Before the route is first read, so the screen opened sits on top of the chat.
plantHome();

const root = document.getElementById('root');
if (!root) throw new Error('no #root');

createRoot(root).render(
  <Crash>
    <App />
  </Crash>,
);

// Registered after mount so a failed registration cannot block first paint.
void registerServiceWorker().catch(() => undefined);

// Anything still queued from a previous session goes up now. This is the
// answer to "finish uploads after the app closes": iOS cannot continue an
// upload in the background, so the bytes are kept and resumed on reopen.
void resumeUploads().catch(() => undefined);

// And again whenever the app comes back to the foreground, since
// backgrounding kills the request but usually leaves this page loaded.
watchForResume();

// The same backgrounding kills the Firestore connection, and the local cache
// hides it by answering every listener from yesterday's data.
resyncOnResume();
// Pictures refused while the photo session was being renewed, asked for again.
// A refusal on any screen renews it, at most twice a minute.
watchBrokenMedia(MEDIA_GATEWAY, document, () => void renewMediaSession().catch(() => undefined));
// And a session that ran out while the app was in the background is replaced
// on the way back in, before anything asks on it.
keepMediaSessionFresh();
